import React, { CSSProperties, useState } from 'react';
import { Button, Flexbox, Icon, Tooltip, Typography } from '@hiiretail/synergy-ui';
import { CopyToClipboard as CopyComponent } from 'react-copy-to-clipboard';
import styled from 'styled-components';
import {
  TypographyProps,
  TypographyVariant,
} from '@hiiretail/synergy-ui/dist/types/components/content/Typography/Typography.types';

const DOT = '\u{25CF}';
const DOTS = DOT.repeat(30);

type CopyToClipboardProps = {
  text: string;
  variant: TypographyVariant;
  ellipsis?: boolean;
  showTooltip?: boolean;
  hideText?: boolean;
  textStyle?: CSSProperties;
  iconStyle?: CSSProperties;
} & TypographyProps;

const ShowHideButton = styled(Button)`
  min-width: 65px;
`;

type CopyToClipboardComponent = React.FC<CopyToClipboardProps>;

const CopyToClipboardWrapper = styled(Flexbox)`
  overflow: hidden;
`;

const StyledTypography = styled(Typography)<{ ellipsis: boolean }>`
  ${({ ellipsis }) =>
    ellipsis ? 'text-overflow: ellipsis; white-space: nowrap;' : 'word-break: break-all;'}
  width: 100%;
  overflow: hidden;
`;

const COPY_TEXT = 'Copy to clipboard';
const COPIED_TEXT = 'Copied';

export const CopyToClipboard: CopyToClipboardComponent = ({
  text,
  variant,
  ellipsis = false,
  weight,
  showTooltip = false,
  textStyle = {},
  iconStyle = {},
  hideText = false,
}) => {
  const [isTextHidden, setIsTextHidden] = useState(hideText);
  const [tooltipText, setTooltipText] = useState(COPY_TEXT);
  const handleCopy = () => {
    setTooltipText(COPIED_TEXT);
    setTimeout(() => {
      setTooltipText(COPY_TEXT);
    }, 800);
  };

  return (
    <CopyToClipboardWrapper
      style={{
        display: 'flex',
        width: '100%',
        justifyContent: 'space-between',
        gap: '8px',
      }}
      container
      alignItems="center"
      gutter={2}
    >
      <Flexbox
        item
        container
        style={{
          overflow: 'hidden',
          whiteSpace: ellipsis ? 'nowrap' : 'normal',
          ...textStyle,
        }}
      >
        {showTooltip ? (
          <Tooltip
            title={
              <Typography variant="caption" color={() => 'white'}>
                {isTextHidden ? DOTS : text}
              </Typography>
            }
          >
            {/*@ts-ignore*/}
            <StyledTypography ellipsis={ellipsis} variant={variant} weight={weight}>
              {isTextHidden ? DOTS : text}
            </StyledTypography>
          </Tooltip>
        ) : (
          //@ts-ignore
          <StyledTypography ellipsis={ellipsis} variant={variant} weight={weight}>
            {isTextHidden ? DOTS : text}
          </StyledTypography>
        )}
      </Flexbox>
      <Flexbox item container style={{ overflow: 'hidden', ...iconStyle }}>
        {hideText && (
          <ShowHideButton variant="icon" onClick={() => setIsTextHidden((prev) => !prev)}>
            {isTextHidden ? 'Show' : 'Hide'}
          </ShowHideButton>
        )}
        <Tooltip
          title={
            <Typography variant="caption" color={() => 'white'}>
              {tooltipText}
            </Typography>
          }
        >
          <CopyComponent text={text} onCopy={handleCopy}>
            <Button variant="icon" onClick={(e) => e.stopPropagation()}>
              <Icon.Copy size="sm" bold={weight === 'bold'} />
            </Button>
          </CopyComponent>
        </Tooltip>
      </Flexbox>
    </CopyToClipboardWrapper>
  );
};
